import _ from 'lodash';

import { makePrivateRequestAsUser } from './managers/requestBuilder';
import { analyticsSchemes } from '../utils/cameras';
import { getRegionForRequest } from './managers/store';

/**
 * @typedef PeopleOccupancyPeriod
 * @type {object}
 * @property {string} uidd Uidd of camera.
 * @property {number} uid Owner of camera.
 * @property {number} deviceId Device id of camera.
 * @property {number} firstTimestamp Timestamp in ms of earliest minute with data that contributed to this period.
 * @property {number} lastTimestamp Timestamp in ms of latest minute with data that contributed to this period.
 * @property {number} totalIn Number of people which entered the area in the time period.
 * @property {number} totalOut Number of people which exited the area in the time period.
 */

// Version of `getPeopleOccupancyData` below without formatting data. Use for CSV downloads.
export const getPeopleOccupancyDataRaw = makePrivateRequestAsUser(
    async (
        { makePrivateRequest },
        lines,
        startTime,
        {
            endTime,
            interval,
            timezone,
            onDownloadProgress,
            abortSignal
        } = {}
    ) => {

        const { data } = await makePrivateRequest(
            'analytics',
            'POST',
            '/people',
            {
                data: {
                    // MongoDB data doesn't include line id yet so just search by camera
                    uidds: _.uniq(lines.map(({ uidd }) => uidd)),
                    startTime,
                    endTime,
                    interval,
                    timeZone: timezone
                },
                signal: abortSignal,
                onDownloadProgress,
                splitByRegion: true
            }
        );

        return data;
    }
);

/**
 * Get people occupancy data for specified line crossings across specified time period.
 * Results will currently be grouped by camera as MongoDB has no knowledge of line id.
 * Results can be further grouped by time interval.
 * @param {LineCrossing[]} lines Lines to search across.
 * @param {number} startTime Start of time range to search across.
 * @param {object} [options] Additional options.
 * @param {number} [options.endTime] End of time range to search across.
 * @param {number|"date"} [options.interval] Time interval to group results by in milliseconds. Can also provide key word "date" here to allow for daylight savings.
 * @param {string} [options.timezone] IANA time zone name. Only used when `interval` is "date".
 * @param {function} [options.onDownloadProgress] `onDownloadProgress` callback to pass to Axios.
 * @param {AbortSignal} [options.abortSignal] Abort signal to cancel the request.
 * @returns {Promise<PeopleOccupancyPeriod[]>} Occupancy data.
 */
export const getPeopleOccupancyData = makePrivateRequestAsUser(
    async (
        { makePrivateRequest },
        ...args
    ) => {

        const data = await getPeopleOccupancyDataRaw(...args);
        
        // MongoDB data doesn't include line id yet so add it in below
        const firstLineForEachCamera = args[0].reduce((acc, { uidd, id }) => {
            acc[uidd] = Math.min(id, acc[uidd] ?? Number.MAX_SAFE_INTEGER);
            return acc;
        }, {});

        return data.map(({ uid, deviceId, in: totalIn, out: totalOut, firstTimestamp, lastTimestamp, lineId }) => ({
            uid,
            deviceId,
            uidd: `${uid}.${deviceId}`,
            totalIn,
            totalOut,
            firstTimestamp: firstTimestamp * 1000,
            lastTimestamp: lastTimestamp * 1000,
            // If no line id, just assign it to the first line for the camera
            lineId: lineId ?? firstLineForEachCamera[`${uid}.${deviceId}`]
        }));
    }
);

/**
 * @typedef LineCrossing
 * @type {object}
 * @property {number} id Line id.
 * @property {string} uidd Camera uidd.
 * @property {number} uid Camera owner.
 * @property {number} deviceId Camera device id.
 * @property {number} x1 X coordinate of start point of line between 0 and 1 inclusive.
 * @property {number} y1 Y coordinate of start point of line between 0 and 1 inclusive.
 * @property {number} x2 X coordinate of end point of line between 0 and 1 inclusive.
 * @property {number} y2 Y coordinate of end point of line between 0 and 1 inclusive.
 * @property {('+', '-')} inDirection Denotes which side of line is "in" direction and opposite is "out". For a line crossing AB, a point C is on the negative side if AB × AC is negative (which is anticlockwise around A for a y axis increasing downwards) and positive if AB × AC is positive.
 * @property {string} [name] Name assigned to line.
 * @property {string} [description] Description of line.
 * @property {string[]} [suboptimalReason] One or more reasons why the line will possibly give inaccurate results.
 */

/**
 * Get line crossings across one or more cameras.
 * @param {string[]} uidds Camera uidds.
 * @param {AbortSignal} [abortSignal] AbortSignal to pass to Axios.
 * @returns {LineCrossing[]} Line crossings.
 */
export const getLineCrossings = makePrivateRequestAsUser(async ({ makePrivateRequest }, uidds, abortSignal) => {

    const { data } = await makePrivateRequest(
        'auth',
        'POST',
        `/analytics/lines`,
        {
            splitByRegion: true,
            data: {
                uidd: uidds
            },
            signal: abortSignal
        }
    );

    return data.map(({ uid, deviceId, startX, startY, endX, endY, positiveDirection, ...line }) => ({
        uidd: `${uid}.${deviceId}`,
        uid,
        deviceId,
        x1: startX / 100,
        y1: startY / 100,
        x2: endX / 100,
        y2: endY / 100,
        inDirection: positiveDirection === 'in' ? '+' : '-',
        ...line
    }));
});

/* ------------------------------ Camera config ----------------------------- */

/**
 * Save new line crossing for a camera.
 * @param {string} uidd Camera uidd.
 * @param {number} x1 X coordinate of start node between 0 and 1 inclusive.
 * @param {number} y1 Y coordinate of start node between 0 and 1 inclusive.
 * @param {number} x2 X coordinate of end node between 0 and 1 inclusive.
 * @param {number} y2 Y coordinate of end node between 0 and 1 inclusive.
 * @param {('+', '-')} inDirection Is the line's "in" direction on the positive or negative side?
 * @param {string} scheme Analytics scheme to use for this line.
 * @param {object} [details] Extra details.
 * @param {string} [details.name] Line name.
 * @param {string} [details.description] Line description.
 * @param {string[]} [details.suboptimalReason] Label this line as suboptimal for the specified reason(s).
 * @param {AbortSignal} [abortSignal] AbortSignal to pass to Axios.
 * @returns {LineCrossing} Newly created line.
 */
export const createCameraLineCrossing = makePrivateRequestAsUser(async ({ makePrivateRequest }, uidd, x1, y1, x2, y2, inDirection, scheme, { name, description, suboptimalReason } = {}, abortSignal) => {
    const [uid, deviceId] = uidd.split('.');
    
    const { data } = await makePrivateRequest(
        'auth',
        'POST',
        `/camera/${uid}/${deviceId}/analytics/lines`,
        {
            data: {
                // Database stores integers for line crossings
                startX: Math.round(x1 * 100),
                startY: Math.round(y1 * 100),
                endX: Math.round(x2 * 100),
                endY: Math.round(y2 * 100),
                positiveDirection: inDirection === '+' ? 'in' : 'out',
                name: name || undefined,
                description: description || undefined,
                suboptimalReason: suboptimalReason ?? undefined,
                scheme: analyticsSchemes[scheme]
            },
            signal: abortSignal,
            region: getRegionForRequest(uid)
        }
    );

    return data;
});

/**
 * Update a saved line for a camera.
 * @param {string} uidd Camera uidd.
 * @param {number} lineId Id of line.
 * @property {number} x1 X coordinate of start node between 0 and 1 inclusive.
 * @property {number} y1 Y coordinate of start node between 0 and 1 inclusive.
 * @property {number} x2 X coordinate of end node between 0 and 1 inclusive.
 * @property {number} y2 Y coordinate of end node between 0 and 1 inclusive.
 * @param {('+', '-')} inDirection Is the line's "in" direction on the positive or negative side?
 * @param {object} [details] Extra details.
 * @param {string} [details.name] Line name.
 * @param {string} [details.description] Line description.
 * @param {string[]} [details.suboptimalReason] Label this line as suboptimal for the specified reason.
 * @param {AbortSignal} [abortSignal] AbortSignal to pass to Axios.
 * @returns Axios promise.
 */
export const updateCameraLineCrossing = makePrivateRequestAsUser(async ({ makePrivateRequest }, uidd, lineId, x1, y1, x2, y2, inDirection, { name, description, suboptimalReason } = {}, abortSignal) => {
    const [uid, deviceId] = uidd.split('.');
    
    const { data } = await makePrivateRequest(
        'auth',
        'PUT',
        `/camera/${uid}/${deviceId}/analytics/lines/${lineId}`,
        {
            data: {
                // Database stores integers for line crossings
                startX: Math.round(x1 * 100),
                startY: Math.round(y1 * 100),
                endX: Math.round(x2 * 100),
                endY: Math.round(y2 * 100),
                positiveDirection: inDirection === '+' ? 'in' : 'out',
                name: name || undefined,
                description: description || undefined,
                suboptimalReason: suboptimalReason ?? undefined
            },
            signal: abortSignal,
            region: getRegionForRequest(uid)
        }
    );

    return data;
});

/**
 * Delete a saved line for a camera.
 * @param {string} uidd Camera uidd.
 * @param {number} lineId Id of line.
 * @param {AbortSignal} [abortSignal] AbortSignal to pass to Axios.
 * @returns Axios promise.
 */
export const deleteCameraLineCrossing = makePrivateRequestAsUser(async ({ makePrivateRequest }, uidd, lineId, abortSignal) => {
    const [uid, deviceId] = uidd.split('.');
    
    const { data } = await makePrivateRequest(
        'auth',
        'DELETE',
        `/camera/${uid}/${deviceId}/analytics/lines/${lineId}`,
        {
            signal: abortSignal,
            region: getRegionForRequest(uid)
        }
    );

    return data;
});